import Vue from 'vue';
import { Commit } from 'vuex';

import { addGameToRecentlyPlayed } from './gaming/mutations';
import { RecentlyPlayedGame } from './gaming/types';
import getters from './getters';
import {
    IUserPreferences,
    IPreferencesState,
    DEFAULT_USER_ID,
    BetslipPreferences,
    PredefinedStake,
} from './types';
import {
    createRemoveFromListMutator,
    createToggleListElementMutator,
    createSimpleMutatorAction,
} from './util';
import { getConfig } from '@superbet-group/web.lib.config';

export type {
    PreferencesGetters,
    IPreferencesState,
} from './types';

const REMOVE_TOURNAMENT = 'REMOVE_TOURNAMENT';
const TOGGLE_TOURNAMENT = 'TOGGLE_TOURNAMENT';
const TOGGLE_MARKET = 'TOGGLE_MARKET';
const INITIALIZE_USER_PREFERENCES = 'INITIALIZE_USER_PREFERENCES';
const UPGRADE_USER_PREFERENCES = 'UPGRADE_USER_PREFERENCES';
const TOGGLE_EVENT_PIN = 'TOGGLE_EVENT_PIN';
const UNPIN_ALL_EVENTS = 'UNPIN_ALL_EVENTS';
const ADD_GAME_TO_RECENTLY_PLAYED = 'ADD_GAME_TO_RECENTLY_PLAYED';
const SET_DELETE_BETSLIP_AFTER_PURCHASE = 'SET_DELETE_BETSLIP_AFTER_PURCHASE';
const SET_PREDEFINED_STAKE = 'SET_DEFAULT_STAKE';

const CURRENT_VERSION = 'v2.3.0';

const defaultBetslipPreferences: BetslipPreferences = {
    shouldDeleteBetslipAfterPurchase: true,
    predefinedStakes: getConfig().betSlip.predefinedStakes?.map((stake, index) => {
        return { value: stake, index };
    }) ?? [],
};

const createInitialUserState = (version: string): IUserPreferences => ({
    version,
    favouriteTournaments: [],
    favouriteMarkets: [],
    pinnedEvents: [],
    recentlyPlayedGames: [],
    betslipPreferences: defaultBetslipPreferences,
});

function defaultState(): IPreferencesState {
    return {
        [DEFAULT_USER_ID]: createInitialUserState(CURRENT_VERSION),
    };
}

const store = {
    getters,

    namespaced: true,

    state: defaultState(),

    mutations: {
        [REMOVE_TOURNAMENT]: createRemoveFromListMutator('favouriteTournaments'),
        [TOGGLE_TOURNAMENT]: createToggleListElementMutator('favouriteTournaments'),
        [TOGGLE_MARKET]: createToggleListElementMutator('favouriteMarkets'),
        [INITIALIZE_USER_PREFERENCES]: (state: IPreferencesState, userId: string) => {
            Vue.set(state, userId, createInitialUserState(CURRENT_VERSION)); // must be updated manually every time a new feature is added
        },
        [UPGRADE_USER_PREFERENCES]: (state: IPreferencesState, userId: string) => {
            const initialState = createInitialUserState(CURRENT_VERSION);
            state[userId] = {
                ...initialState,
                ...state[userId],
            };
        },
        [TOGGLE_EVENT_PIN]: createToggleListElementMutator('pinnedEvents'),
        [UNPIN_ALL_EVENTS]: (state: IPreferencesState, { userId }: { userId: string }) => {
            state[userId].pinnedEvents = [];
        },
        [ADD_GAME_TO_RECENTLY_PLAYED](state: IPreferencesState, { userId, value }: { userId: string; value: RecentlyPlayedGame }) {
            const games = state[userId].recentlyPlayedGames;
            addGameToRecentlyPlayed(games, value);
        },
        [SET_DELETE_BETSLIP_AFTER_PURCHASE](state: IPreferencesState, { userId, value }: { userId: string; value: boolean }) {
            Vue.set(state[userId].betslipPreferences, 'shouldDeleteBetslipAfterPurchase', value);
        },
        [SET_PREDEFINED_STAKE](state: IPreferencesState, { userId, value }: { userId: string; value: PredefinedStake }) {
            const stakeToChange = state[userId].betslipPreferences.predefinedStakes.find((predefinedStake) => predefinedStake.index === value.index);
            if (stakeToChange) {
                stakeToChange.value = value.value;
            }
        },
    },

    actions: {
        initializeUserPreferences: ({ commit, getters, state }:
            {commit: Commit; getters: any; state: IPreferencesState}) => {
            const userId = getters._userId;
            if (!state[userId]) {
                commit(INITIALIZE_USER_PREFERENCES, userId);
            } else if (!state[userId].version || state[userId].version < CURRENT_VERSION) {
                commit(UPGRADE_USER_PREFERENCES, userId);
            }
        },
        removeTournament: createSimpleMutatorAction(REMOVE_TOURNAMENT),
        toggleTournament: createSimpleMutatorAction(TOGGLE_TOURNAMENT),
        toggleMarket: createSimpleMutatorAction(TOGGLE_MARKET),
        toggleEventPin: createSimpleMutatorAction(TOGGLE_EVENT_PIN),
        unpinAllEvents: createSimpleMutatorAction(UNPIN_ALL_EVENTS),
        addGameToRecentlyPlayed: createSimpleMutatorAction(ADD_GAME_TO_RECENTLY_PLAYED),
        setDeleteBetslipAfterPurchase: createSimpleMutatorAction(SET_DELETE_BETSLIP_AFTER_PURCHASE),
        setPredefinedStake: createSimpleMutatorAction(SET_PREDEFINED_STAKE),
    },
};

export type UserPreferencesStore = typeof store;
export default store;
